<template>
  <div class="page-loader app-loader" v-if="showLoader">
    <span class="spinner"></span>
    <span>Loading…</span>
  </div>

  <div class="app-shell" v-else :class="{ 'app-shell--nav-open': navOpen }">
    <AppSidebar class="app-shell__nav" :runningJobs="runningJobs" :collapsed="railMode" @navigate="navOpen = false" />
    <div class="app-shell__scrim" @click="navOpen = false"></div>
    <div class="app-shell__main">
      <div class="app-shell__mobilebar">
        <button class="icon-btn" type="button" aria-label="Open navigation" @click="navOpen = true">
          <AtlasIcon name="menu" />
        </button>
        <span class="app-shell__mobile-title">Marketing</span>
      </div>
      <router-view></router-view>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { MetaService } from "@/model/MetaService"
import AuthStore from "@/store/AuthStore"
import { JobsUrl } from '@/model/api/urls'
import { CommonApi } from '@/model/api/CommonApi'
import { JobsDashboardResp } from '@/model/api/JobsApi'
import AppSidebar from '@/layout/AppSidebar.vue'
import AtlasIcon from '@/component/ui/AtlasIcon.vue'

const route = useRoute()
const showLoader = ref(true)
const navOpen = ref(false)
// Routes with meta.navRail get the icon rail; the mobile drawer always shows the full sidebar.
const railMode = computed(() => !!route.meta.navRail && !navOpen.value)
const runningJobs = ref(0)
let jobsTimer: ReturnType<typeof setInterval> | null = null

// Feeds the "N running" count next to Jobs in the sidebar. Called without
// CommonApi.catchHandler so a failed poll stays silent instead of alerting.
async function pollRunningJobs() {
  try {
    const resp = await axios.get<JobsDashboardResp>(JobsUrl, CommonApi.requestConfig())
    runningJobs.value = Object.keys(resp.data.runningJobs ?? {}).length
  } catch {
    runningJobs.value = 0
  }
}

onMounted(async () => {
  showLoader.value = AuthStore.module.isAuthorized && !MetaService.isMetaLoaded

  if (showLoader.value) {
    await MetaService.updateMeta()
    showLoader.value = !MetaService.isMetaLoaded
  }

  if (AuthStore.module.canSeeJobs) {
    pollRunningJobs()
    jobsTimer = setInterval(pollRunningJobs, 30_000)
  }
})

onUnmounted(() => {
  if (jobsTimer) clearInterval(jobsTimer)
})
</script>

<style scoped>
.app-loader { height: 100%; min-height: 100vh; }

.app-shell {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

.app-shell__nav { position: sticky; top: 0; }

.app-shell__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.app-shell__scrim, .app-shell__mobilebar { display: none; }

@media (max-width: 767px) {
  .app-shell__nav {
    position: fixed; left: 0; top: 0; bottom: 0; z-index: 900;
    transform: translateX(-100%);
    transition: transform var(--duration-base) var(--ease-standard);
    box-shadow: var(--shadow-lg);
  }
  .app-shell--nav-open .app-shell__nav { transform: none; }
  .app-shell--nav-open .app-shell__scrim {
    display: block; position: fixed; inset: 0; z-index: 800; background: rgb(17 24 39 / 0.4);
  }
  .app-shell__mobilebar {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; border-bottom: 1px solid var(--border); background: var(--side);
  }
  .app-shell__mobile-title { font-weight: 600; }
}
</style>
